import styles from "./ContributionForm.module.css";
import { useState, useRef, useEffect } from "react";
import Button from "../Button";
import { selectFilesvg, warningsvg } from "../../assets/svg/svgimages";
import ThankYouMessage from "./ThankYouMessage";
import UploadOverlay from "../UploadOverlay/UploadOverlay";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { uploadProductFile, clearUploadError } from "../../store/slices/singleProductSlice";
import { setAuthRedirectPath } from "../../store/userSlice";
import { saveFileToIDB, loadFileFromIDB, clearFileFromIDB } from "../../utils/fileStorage";

const ContributionForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { status, error } = useSelector((state) => state.singleProduct);
  const { isAuthenticated } = useSelector((state) => state.user);

  const [formValues, setFormValues] = useState(() => {
    const saved = sessionStorage.getItem('contributionFormData');
    if (saved) {
      try { return JSON.parse(saved); } catch { /* ignore */ }
    }
    return { camera: "", lensManufacturer: "", lensModel: "", description: "" };
  });

  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isFileAttached, setIsFileAttached] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fileError, setFileError] = useState(null);

  // Advanced States & Refs for audited plan optimizations
  const [isSavingAndRedirecting, setIsSavingAndRedirecting] = useState(false);
  const [copied, setCopied] = useState(false);
  const uploadPromiseRef = useRef(null);

  const isFormDisabled = status.uploadProductStatus === "loading" || isSavingAndRedirecting;

  useEffect(() => {
    const restoreFile = async () => {
      try {
        const savedFile = await loadFileFromIDB();
        if (savedFile) {
          setFile(savedFile);
          setIsFileAttached(true);
          await clearFileFromIDB();
          sessionStorage.removeItem('contributionFormData');
        }
      } catch (e) {
        console.error('Failed to restore file from IndexedDB:', e);
      }
    };
    restoreFile();
  }, []);

  // Cleanup abort controller on unmount to prevent leaks
  useEffect(() => {
    return () => {
      if (uploadPromiseRef.current) {
        uploadPromiseRef.current.abort();
      }
    };
  }, []);

  // Tab Closure Safeguard
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDisabled) {
        e.preventDefault();
        e.returnValue = "An upload is in progress. Are you sure you want to leave?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFormDisabled]);

  // Reference for the file input
  const fileInputRef = useRef(null);

  // Handling input field changes
  const handleChange = (e) => {
    if (isFormDisabled) return;
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    if (fileError) setFileError(null);
  };

  const handleFileChange = (e) => {
    if (isFormDisabled) return;
    const selectedFile = e.target.files[0];

    if (!selectedFile) {
      return;
    }

    const MAX_FILE_SIZE = 500 * 1024 * 1024;
    if (selectedFile.size > MAX_FILE_SIZE) {
      setFileError('File size exceeds the 500 MB limit');
      return;
    }

    if (!selectedFile.name.toLowerCase().endsWith('.zip')) {
      setFileError('Only ZIP files are allowed');
      return;
    }

    setFileError(null);
    setFile(selectedFile);
    setIsFileAttached(true);
    setIsUploading(true);

    setTimeout(() => {
      setIsUploading(false);
    }, 1000);
  };

  // Triggering the file input when the Import button is clicked
  const handleFileClick = () => {
    if (isFormDisabled) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCancelUpload = () => {
    if (uploadPromiseRef.current) {
      uploadPromiseRef.current.abort();
      uploadPromiseRef.current = null;
    }
  };

  const handleCopyError = () => {
    const details = error.uploadProductErrorDetails;
    if (!details) return;

    const rawResponse = typeof details.responseText === "string"
      ? details.responseText
      : JSON.stringify(details.responseText, null, 2);

    // Scrub secret JWT signatures completely from logs
    const sanitizedResponse = rawResponse.replace(
      /Bearer\s[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*/g,
      "Bearer [SCRUBBED]"
    );

    const markdownLog = [
      "================ Mm_GridsDB UPLOAD ERROR REPORT ================",
      `Timestamp: ${details.timestamp || new Date().toISOString()}`,
      `Page URL: ${window.location.pathname}`,
      `API Endpoint: ${details.url || "/products/v1/contributions/"}`,
      `Status Code: ${details.status || "0"} (${details.statusText || "Network Error"})`,
      "----------------------------------------------------------------",
      `Error Message: ${error.uploadProductError || "Unknown"}`,
      "----------------------------------------------------------------",
      "Response Payload:",
      sanitizedResponse,
      "================================================================"
    ].join("\n");

    const copyTextToClipboard = (text) => {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        return new Promise((resolve, reject) => {
          try {
            const successful = document.execCommand('copy');
            if (successful) resolve();
            else reject(new Error("Fallback copy command failed"));
          } catch (err) {
            reject(err);
          } finally {
            document.body.removeChild(textArea);
          }
        });
      }
    };

    copyTextToClipboard(markdownLog)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Clipboard copy failure:", err);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isFormDisabled) return; // Prevent double submits

    dispatch(clearUploadError());

    if(!isAuthenticated) {
      setIsSavingAndRedirecting(true);
      sessionStorage.setItem('contributionFormData', JSON.stringify(formValues));
      const saveAndRedirect = async () => {
        if (file) {
          try {
            await saveFileToIDB(file);
          } catch (e) {
            console.error('Failed to save file to IndexedDB:', e);
            alert("Could not temporarily cache the file in the browser (Safari Private Mode or out of space). You will need to select the file again after signing in.");
          }
        }
        dispatch(setAuthRedirectPath(location.pathname + location.search));
        navigate("/authorization");
      };
      saveAndRedirect();
      return;
    }

    // Валидация полей
    if (!formValues.camera.trim() || !formValues.lensManufacturer.trim() || !formValues.lensModel.trim() || !formValues.description.trim()) {
      setFileError('Please fill in all fields');
      return;
    }

    // Ensure a file is selected
    if (!file) {
      setFileError('Please select a file to upload.');
      return;
    }

    const promise = dispatch(uploadProductFile({ formData: formValues, file }));
    uploadPromiseRef.current = promise;

    promise.unwrap()
      .then(() => {
        // On success, show ThankYouMessage
        setSubmitted(true);
        // Reset the form and file input after successful submission
        setFormValues({
          camera: "",
          lensManufacturer: "",
          lensModel: "",
          description: "",
        });
        setFile(null);
        setIsFileAttached(false);
      })
      .catch((err) => {
        console.error("File upload failed or was aborted: ", err);
      });
  };

  // Drag and Drop Handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isFormDisabled) return;

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isFormDisabled) return;

    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];

      const MAX_FILE_SIZE = 500 * 1024 * 1024;
      if (droppedFile.size > MAX_FILE_SIZE) {
        setFileError('File size exceeds the 500 MB limit');
        return;
      }

      if (!droppedFile.name.toLowerCase().endsWith('.zip')) {
        setFileError('Only ZIP files are allowed');
        return;
      }
      setFileError(null);
      setFile(droppedFile);
      setIsFileAttached(true);
      setIsUploading(true);

      setTimeout(() => {
        setIsUploading(false);
      }, 1000);
    }
  };

  if (submitted) {
    return <ThankYouMessage />;
  }

  const uploadError = error.uploadProductError;

  return (
    <>
      {/* Visual Overlay during active loading/upload process */}
      {status.uploadProductStatus === "loading" && (
        <UploadOverlay
          percent={status.uploadProgress}
          loaded={status.uploadLoaded}
          total={status.uploadTotal}
          onCancel={handleCancelUpload}
        />
      )}

      <form onSubmit={handleSubmit}>
        {/* Customized Native Error alert matching site aesthetics */}
        {status.uploadProductStatus === "failed" && uploadError && (
          <div className={styles.errorBlock}>
            <div className="error-message">
              {warningsvg}
              <span>Upload failed: {uploadError}</span>
            </div>
            <p className={styles.errorHint}>
              Please check your internet connection and try again, or click below to copy technical logs for support.
            </p>
            <button
              type="button"
              className={`${styles.copyErrorBtn} ${copied ? styles.copyErrorBtnActive : ""}`}
              onClick={handleCopyError}
            >
              {copied ? "Copied!" : "Copy error details"}
            </button>
          </div>
        )}

        <div className={styles.formCont}>
          <div className={styles.form}>
            <input
              type="text"
              id="camera"
              name="camera"
              value={formValues.camera}
              onChange={handleChange}
              placeholder="Camera Manufacturer"
              required
              disabled={isFormDisabled}
            />

            <input
              type="text"
              id="lensManufacturer"
              name="lensManufacturer"
              value={formValues.lensManufacturer}
              onChange={handleChange}
              placeholder="Lens Manufacturer"
              required
              disabled={isFormDisabled}
            />

            <input
              type="text"
              id="lensModel"
              name="lensModel"
              placeholder="Lens Model"
              value={formValues.lensModel}
              onChange={handleChange}
              required
              disabled={isFormDisabled}
            />

            <textarea
              id="description"
              name="description"
              placeholder="Description"
              value={formValues.description}
              onChange={handleChange}
              rows="4"
              required
              disabled={isFormDisabled}
            />

            <Button
              variant={isFormDisabled ? "grey" : "blue"}
              label={
                isSavingAndRedirecting ? "Saving file..." :
                status.uploadProductStatus === "loading" ? "Sending..." :
                isAuthenticated ? "Send" : "Sign in/Register to Send"
              }
              iconType="arrowRight"
              onClick={handleSubmit}
              disabled={isFormDisabled}
            />
          </div>

          <div
            className={`${styles.atachment} ${dragActive ? styles.atachmentActive : ""} ${isFormDisabled ? styles.atachmentDisabled : ""}`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
          >
            <div className={styles.attachCont}>
              {selectFilesvg}
              <p className={styles.selectmessage}>
                Select a file from local drive or <span>drag it here</span>
              </p>

              {/* Indication when file is attached and uploading */}
              {isFileAttached && !isUploading && file && (
                <div className={styles.fileAttached}>
                  <p>File attached: {file.name}</p>
                </div>
              )}

              {isUploading && (
                <div className={styles.fileUploading}>
                  <p>Attaching file...</p>
                  <div className={styles.loader}></div>
                </div>
              )}
              {fileError && (
                <p style={{ color: '#ff6b6b', fontSize: '14px', marginTop: '5px' }}>{fileError}</p>
              )}
              <p className={styles.caution}>
                (Only ZIP files, max 500 MB)
              </p>
            </div>
            <div className={styles.btn}>
              <input
                type="file"
                id="fileInput"
                accept=".zip"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                disabled={isFormDisabled}
              />
              <Button
                variant="blue"
                label="Import"
                iconType="arrowRight"
                onClick={handleFileClick}
                disabled={isFormDisabled}
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default ContributionForm;